<script setup>
import { ref } from 'vue';
import { useSync } from '../sync.js';

const { isLoadingAfterLogin, loginSyncFailed, syncAll } = useSync();

const retrying = ref(false);

// Non-silent so this attempt's own outcome gets a toast either way (see
// account_widget.vue's onSync for the same pattern) — the automatic
// attempt that got us into loginSyncFailed was silent and gave no
// feedback, which is exactly the confusing gap this whole component
// exists to close.
async function retry() {
  retrying.value = true;
  try {
    await syncAll({ silent: false });
  } finally {
    retrying.value = false;
  }
}
</script>

<template>
  <div v-if="isLoadingAfterLogin" class="login-sync-overlay">
    <div class="login-sync-box">
      <span class="spinner"></span>
      Loading your setups…
    </div>
  </div>
  <div v-else-if="loginSyncFailed" class="login-sync-overlay">
    <div class="login-sync-box login-sync-box-error">
      Couldn't load your setups.
      <button type="button" class="login-sync-retry" @click="retry" :disabled="retrying">
        {{ retrying ? 'Retrying…' : 'Retry' }}
      </button>
    </div>
  </div>
</template>

<style scoped>

/* Same shape as computing_indicator.vue's overlay — non-blocking (the app
   is fully usable, and editable, while this shows; the setup it's about to
   replace is just discardLocalIdentity's bare bootstrap "Default", not
   anything the user typed), just present enough that landing on a fresh
   "Default" right after logging in reads as "still loading" (or "failed,
   here's a retry") rather than "my setup got reset". */
.login-sync-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: var(--space-lg);
  pointer-events: none;
}

.login-sync-box {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
  background: var(--color-primary);
  color: var(--color-text-light);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-md);
  font-weight: var(--font-weight-semibold);
}

/* The Retry button is the one interactive part of an otherwise
   click-through overlay — re-enable pointer events just for it rather than
   the whole overlay, so it doesn't block clicks on whatever's underneath
   while this banner is up. */
.login-sync-box-error {
  background: var(--color-danger);
  pointer-events: auto;
}

.login-sync-retry {
  padding: var(--space-xs) var(--space-sm);
  background: var(--color-text-light);
  color: var(--color-text);
  border: none;
  border-radius: var(--border-radius);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.login-sync-retry:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: login-sync-spin 0.7s linear infinite;
}

@keyframes login-sync-spin {
  to { transform: rotate(360deg); }
}

</style>
